import {
  type HhDataItem,
  SiteDemandView,
  useDemandData,
  useSite,
} from "@inrange/building-manager-api-client";
import { useEffect, useMemo, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import Select from "react-select";
import HhDataRow from "./HhDataRow";

interface HhDataTableProps {
  items: HhDataItem[];
  siteMap: Map<string, SiteDemandView>;
  showActions?: boolean;
  onEditClick?: (item: HhDataItem) => void;
  onViewConfigClick?: (item: HhDataItem) => void;
  onAssignToSitesClick?: (item: HhDataItem) => void;
  onRemoveFromSitesClick?: (item: HhDataItem) => void;
  onDeleteClick?: (item: HhDataItem) => void;
  onReviewAnalysisClick?: (item: HhDataItem) => void;
  selectedItems?: HhDataItem[];
  onSelectionChange?: (items: HhDataItem[]) => void;
  initialFilterText?: string;
}

type SortOrder = "processedTime" | "siteName";
type SortDirection = "asc" | "desc";

const sortOrderLabels: Record<SortOrder, [string, SortDirection]> = {
  processedTime: ["Processed at", "desc"],
  siteName: ["Site name", "asc"],
};

const HhDataTable = ({
  items,
  siteMap,
  showActions = true,
  onEditClick,
  onViewConfigClick,
  onAssignToSitesClick,
  onRemoveFromSitesClick,
  onDeleteClick,
  onReviewAnalysisClick,
  selectedItems = [],
  onSelectionChange,
  initialFilterText = "",
}: HhDataTableProps) => {
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const { fetchHhDataDownloadUrl } = useDemandData();

  const [filteredOrgIds, setFilteredOrgIds] = useState<string[]>([]);
  const [filteredBuildingProfiles, setFilteredBuildingProfiles] = useState<
    string[]
  >([]);
  const [filterText, setFilterText] = useState<string>(initialFilterText);
  const [debouncedFilterText, setDebouncedFilterText] =
    useState<string>(initialFilterText);
  const [scenarioSiteId, setScenarioSiteId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("processedTime");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

  // Debounce filter text updates
  useEffect(() => {
    const debounceWaitMillis = testMode ? 0 : 300;
    const timeoutId = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, debounceWaitMillis);
    return () => clearTimeout(timeoutId);
  }, [filterText, testMode]);

  // Build list of unique organisations for filtering
  const filterOrgs = useMemo(() => {
    const orgMap = new Map<string, string>();
    for (const site of siteMap.values()) {
      for (const ownership of site.siteOwnerships) {
        if (ownership.name && !orgMap.has(ownership.orgID)) {
          orgMap.set(ownership.orgID, ownership.name);
        }
      }
    }
    return Array.from(orgMap.entries())
      .map(([orgId, name]) => ({ orgId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [siteMap]);

  // Build list of unique building profiles for filtering with labels from fetchSiteValues
  const filterBuildingProfiles = useMemo(() => {
    const profiles = new Set<string>();
    for (const site of siteMap.values()) {
      for (const demand of Object.values(site.demand)) {
        if (demand.buildingProfile) {
          profiles.add(demand.buildingProfile);
        }
      }
    }

    const buildingProfilesData =
      fetchSiteValues.data?.options?.buildingProfiles;

    return Array.from(profiles)
      .map((profileValue) => ({
        value: profileValue,
        label: buildingProfilesData?.[profileValue]?.name || profileValue,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [fetchSiteValues.data?.options?.buildingProfiles, siteMap]);

  // Build list of sites for scenario filtering (sorted by name)
  const scenarioSiteOptions = useMemo(() => {
    return Array.from(
      items
        .flatMap((item) => item.siteMappings)
        .map((siteMapping) => ({
          siteId: siteMapping.site.id,
          name: siteMapping.site.name,
        }))
        .reduce(
          (acc, curr) => acc.set(curr.siteId, curr),
          new Map<string, { siteId: string; name: string }>()
        )
        .values()
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Get the set of site IDs to show when scenario filter is active
  const scenarioSiteIds = useMemo(() => {
    if (!scenarioSiteId) return null;
    const site = siteMap.get(scenarioSiteId);
    if (!site) return null;
    const ids = new Set<string>([site.id, ...(site.nearbySiteIDs || [])]);
    return ids;
  }, [scenarioSiteId, siteMap]);

  // Helper to get site names for an item (for search and sorting)
  const getSiteNamesForItem = (item: HhDataItem): string => {
    return item.siteMappings
      .map((mapping) => {
        const site = mapping.site;
        return (site?.name || "").trim().replaceAll(" ", "").toLowerCase();
      })
      .join(" ")
      .toLowerCase();
  };

  // Filter and sort items based on all filters
  const filteredItems = useMemo(() => {
    const filterWords = debouncedFilterText.toLowerCase().split(" ");

    const filtered = items.filter((item) => {
      // Filter by search text (matches file name, site names, or GUID)
      if (debouncedFilterText) {
        const searchText = `${(item.fileName || "").toLowerCase()} ${getSiteNamesForItem(item)} ${item.guid.toLowerCase()}`;
        if (!filterWords.every((word) => searchText.includes(word))) {
          return false;
        }
      }

      // Filter by scenario site
      if (scenarioSiteIds) {
        const hasMatchingSite = item.siteMappings.some((mapping) =>
          scenarioSiteIds.has(mapping.site.id)
        );
        if (!hasMatchingSite) {
          return false;
        }
      }

      // If no org or building profile filters, include all remaining items
      if (
        filteredOrgIds.length === 0 &&
        filteredBuildingProfiles.length === 0
      ) {
        return true;
      }

      // Check if any of the item's sites match the org/building profile filters
      for (const siteMapping of item.siteMappings) {
        const site = siteMapping.site;
        if (!site) continue;

        // Check org filter
        if (filteredOrgIds.length > 0) {
          const siteOrgIds = site.siteOwnerships.map((o) => o.orgID);
          if (!filteredOrgIds.some((orgId) => siteOrgIds.includes(orgId))) {
            continue;
          }
        }

        // Check building profile filter
        if (filteredBuildingProfiles.length > 0) {
          const demand = site.demand[siteMapping.demandIdx];
          if (
            !demand ||
            !filteredBuildingProfiles.includes(demand.buildingProfile)
          ) {
            continue;
          }
        }

        // This site matches all filters
        return true;
      }
      return false;
    });

    // Sort filtered items
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortOrder === "processedTime") {
        comparison = (a.processedTime || 0) - (b.processedTime || 0);
      } else if (sortOrder === "siteName") {
        const aName = getSiteNamesForItem(a);
        const bName = getSiteNamesForItem(b);
        comparison = aName.localeCompare(bName);
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [
    debouncedFilterText,
    items,
    scenarioSiteIds,
    filteredOrgIds,
    filteredBuildingProfiles,
    sortOrder,
    sortDirection,
  ]);

  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? filteredItems : []);
    }
  };

  const handleSelectItem = (item: HhDataItem, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedItems, item]);
      } else {
        onSelectionChange(selectedItems.filter((i) => i.guid !== item.guid));
      }
    }
  };

  const allSelected =
    filteredItems.length > 0 && selectedItems.length === filteredItems.length;
  const someSelected =
    selectedItems.length > 0 && selectedItems.length < filteredItems.length;

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "16px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Form.Control
          type="search"
          placeholder="Search sites..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{ width: "200px" }}
        />
        <Select
          placeholder="Filter by organisation..."
          onChange={(values) => {
            setFilteredOrgIds(
              values ? values.map((v: { value: string }) => v.value) : []
            );
          }}
          options={filterOrgs.map((org) => ({
            label: org.name,
            value: org.orgId,
          }))}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              border: "var(--bs-border-width) solid var(--bs-border-color)",
              borderRadius: "var(--bs-border-radius)",
              color: "var(--bs-body-color)",
              minWidth: "250px",
            }),
          }}
          isMulti
          isClearable
        />
        <Select
          placeholder="Filter by building profile..."
          onChange={(values) => {
            setFilteredBuildingProfiles(
              values ? values.map((v: { value: string }) => v.value) : []
            );
          }}
          options={filterBuildingProfiles}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              border: "var(--bs-border-width) solid var(--bs-border-color)",
              borderRadius: "var(--bs-border-radius)",
              color: "var(--bs-body-color)",
              minWidth: "200px",
            }),
          }}
          isMulti
          isClearable
        />
        <Select
          placeholder="Filter by scenarios of site..."
          onChange={(value: { value: string } | null) => {
            setScenarioSiteId(value ? value.value : null);
          }}
          options={scenarioSiteOptions.map((site) => ({
            label: site.name,
            value: site.siteId,
          }))}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              border: "var(--bs-border-width) solid var(--bs-border-color)",
              borderRadius: "var(--bs-border-radius)",
              color: "var(--bs-body-color)",
              minWidth: "250px",
            }),
          }}
          isClearable
        />
        <div style={{ flexGrow: 1 }} />
        <Form.Group controlId="sortBy" className="d-flex align-items-center">
          <Form.Label className="me-2 mb-0" style={{ whiteSpace: "nowrap" }}>
            Sort by
          </Form.Label>
          <Form.Select
            value={sortOrder}
            onChange={(e) => {
              const newOrder = e.target.value as SortOrder;
              setSortOrder(newOrder);
              setSortDirection(sortOrderLabels[newOrder][1]);
            }}
          >
            {(Object.keys(sortOrderLabels) as SortOrder[]).map((key) => (
              <option key={key} value={key}>
                {sortOrderLabels[key][0]}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Button
          variant="primary"
          onClick={() =>
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
          }
        >
          {sortDirection === "asc" ? "↑" : "↓"}
        </Button>
        <span style={{ fontSize: "14px", color: "#666" }}>
          Showing {filteredItems.length} of {items.length} items
        </span>
      </div>
      <Table bordered size="sm" responsive style={{ marginTop: "8px" }}>
        <thead>
          <tr>
            {onSelectionChange && (
              <th style={{ width: "40px" }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            <th>File Name</th>
            <th>Uploaded by</th>
            <th>Processed at</th>
            <th title="% of the normalised year derived directly from customer data">
              Customer Data %
            </th>
            <th>Sites</th>
            {showActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item) => (
            <HhDataRow
              key={item.guid}
              item={item}
              onEditClick={onEditClick}
              onViewConfigClick={onViewConfigClick}
              onAssignToSitesClick={onAssignToSitesClick}
              onRemoveFromSitesClick={onRemoveFromSitesClick}
              onDeleteClick={onDeleteClick}
              onReviewAnalysisClick={onReviewAnalysisClick}
              onDownloadClick={(item) =>
                fetchHhDataDownloadUrl.mutateAsync(item.guid)
              }
              showActions={showActions}
              isSelected={selectedItems.some((i) => i.guid === item.guid)}
              onSelectionChange={
                onSelectionChange
                  ? (checked) => handleSelectItem(item, checked)
                  : undefined
              }
            />
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default HhDataTable;
